import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ModalFormWrapper } from '@/components/ui/ModalFormWrapper';
import { InvitationCreate, InvitationCreateSchema, UserRole } from '@/schema/invitation';
import { useCreateInvitation } from '@/hooks/useInvitations';
import { useAdminClinics } from '@/hooks/useAdmin';
import { InviteLinkPanel } from './InviteLinkPanel';

/** Every clinic fits in one select; there is no paging UI for a dropdown. */
const CLINIC_OPTIONS_SIZE = 200;

interface InviteDialogProps {
  onClose: () => void;
  /** Pre-selects the clinic — used right after one is created. */
  defaultClinicId?: string;
  defaultRole?: UserRole;
}

export function InviteDialog({ onClose, defaultClinicId, defaultRole = UserRole.user }: InviteDialogProps) {
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const createInvitation = useCreateInvitation();
  const { data: clinics } = useAdminClinics(0, CLINIC_OPTIONS_SIZE);

  const form = useForm<InvitationCreate>({
    resolver: zodResolver(InvitationCreateSchema),
    defaultValues: {
      email: '',
      role: defaultRole,
      clinicId: defaultClinicId ?? '',
      firstName: '',
      lastName: '',
      phone: '',
      note: '',
      expiresInDays: 14,
    },
  });

  const role = form.watch('role');

  const handleSubmit = (data: InvitationCreate) => {
    createInvitation.mutate(
      { ...data, clinicId: data.clinicId || undefined },
      {
        onSuccess: (invitation) => setInviteUrl(invitation.inviteUrl),
        onError: (error) => {
          const message = error instanceof Error ? error.message : 'Could not create the invitation';
          toast.error(message);
        },
      },
    );
  };

  // Once the link exists the form is done; showing it is the whole point of the flow,
  // because nothing e-mails it for us.
  if (inviteUrl) {
    return <InviteLinkPanel inviteUrl={inviteUrl} onClose={onClose} />;
  }

  return (
    <ModalFormWrapper
      form={form}
      submitText={createInvitation.isPending ? 'Creating…' : 'Create invite link'}
      submitButton="default"
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      <Form {...form}>
        <div className="flex flex-col gap-5 text-white">
          <header className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/60">Invite a person</p>
            <p className="text-sm text-white/55">
              The clinic and role are fixed here — the invitee only picks a username and password.
            </p>
          </header>

          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail *</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Pick a role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value={UserRole.user}>Clinic user</SelectItem>
                        <SelectItem value={UserRole.admin}>Administrator (read-only)</SelectItem>
                        <SelectItem value={UserRole.superadmin}>Superadmin</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="clinicId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{role === UserRole.user ? 'Clinic *' : 'Clinic'}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || ''}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Pick a clinic" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectGroup>
                      {(clinics?.content ?? []).map((clinic) => (
                        <SelectItem key={clinic.id} value={clinic.id}>
                          {clinic.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid gap-4 md:grid-cols-3">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First name</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last name</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input type="tel" {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="expiresInDays"
            render={({ field }) => (
              <FormItem className="max-w-40">
                <FormLabel>Valid for (days)</FormLabel>
                <FormControl>
                  <Input type="number" min={1} max={90} {...field} value={field.value ?? 14} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </Form>
    </ModalFormWrapper>
  );
}
